import { Component, OnInit, OnDestroy, inject, signal, ChangeDetectorRef, ApplicationRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { NotificationService, Notification } from '../../core/services/notification.service';

@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="notification-container" aria-live="polite">
      @for (n of items(); track n.id) {
        <div class="toast toast--{{ n.type }}" role="alert">
          <div class="toast-content">
            <span class="toast-message">{{ n.message }}</span>
            @if (n.detail) {
              <div class="toast-detail">{{ n.detail }}</div>
            }
          </div>
          <button type="button" class="toast-close" (click)="dismiss(n.id)" aria-label="Закрыть">&times;</button>
        </div>
      }
    </div>
  `,
  styles: [`
    .notification-container {
      position: fixed;
      top: 16px;
      right: 16px;
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-width: 420px;
    }
    .toast {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      background: var(--surface, #fff);
      color: var(--text-primary, #1a1a1a);
      border-left: 4px solid;
    }
    .toast--success { border-left-color: var(--success, #16a34a); }
    .toast--error   { border-left-color: var(--error, #dc2626); }
    .toast--info    { border-left-color: var(--primary, #2563eb); }
    .toast-content { flex: 1; min-width: 0; }
    .toast-message { font-weight: 600; }
    .toast-detail  { margin-top: 6px; font-size: 0.9rem; opacity: 0.9; white-space: pre-wrap; }
    .toast-close {
      flex-shrink: 0;
      background: none;
      border: none;
      font-size: 1.25rem;
      line-height: 1;
      cursor: pointer;
      color: var(--text-primary, #333);
      opacity: 0.8;
      padding: 2px 6px;
      border-radius: 4px;
    }
    .toast-close:hover { opacity: 1; background: rgba(0,0,0,0.08); }
  `]
})
export class NotificationComponent implements OnInit, OnDestroy {
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private appRef = inject(ApplicationRef);
  private router = inject(Router);
  private sub: { unsubscribe: () => void } | null = null;
  private routerSub: Subscription | null = null;
  private timeouts = new Map<string, ReturnType<typeof setTimeout>>();

  protected items = signal<Notification[]>([]);

  ngOnInit(): void {
    this.sub = this.notificationService.notifications.subscribe((n) => {
      this.items.update((list) => [...list, n]);
      if (n.autoCloseMs && n.autoCloseMs > 0) {
        const t = setTimeout(() => this.dismiss(n.id), n.autoCloseMs);
        this.timeouts.set(n.id, t);
      }
      this.cdr.detectChanges();
      this.appRef.tick();
    });

    this.routerSub = this.router.events.pipe(
      filter(e => e instanceof NavigationStart)
    ).subscribe(() => {
      this.items.set([]);
      this.timeouts.forEach(t => clearTimeout(t));
      this.timeouts.clear();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.timeouts.forEach((t) => clearTimeout(t));
    this.timeouts.clear();
  }

  dismiss(id: string): void {
    const t = this.timeouts.get(id);
    if (t) {
      clearTimeout(t);
      this.timeouts.delete(id);
    }
    this.items.update((list) => list.filter((item) => item.id !== id));
    this.cdr.detectChanges();
  }
}
